import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Shield, User as UserIcon } from "lucide-react";

export default function AdminUsers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);

  const load = () => api.get("/admin/users").then(({ data }) => { setItems(data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const update = async (id, payload) => {
    try {
      await api.patch(`/admin/users/${id}`, payload);
      toast.success("Benutzer aktualisiert");
      load();
    } catch { toast.error("Fehler"); }
  };

  const del = async (id) => {
    if (!window.confirm("Benutzer wirklich löschen?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Gelöscht");
      load();
      setSel(null);
    } catch { toast.error("Fehler"); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-8" data-testid="admin-users">
      <h1 className="font-heading text-3xl font-bold text-[#1A2433] mb-2">Benutzerverwaltung</h1>
      <p className="text-slate-600 mb-6">{items.length} Benutzer.</p>

      {loading ? <div className="text-slate-500">Lade…</div> : (
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">E-Mail</th>
                <th className="px-4 py-3 font-semibold">Firma</th>
                <th className="px-4 py-3 font-semibold">Rolle</th>
                <th className="px-4 py-3 font-semibold">Registriert</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-[#1A2433]">{u.first_name} {u.last_name}</td>
                  <td className="px-4 py-3 text-slate-600">{u.email}</td>
                  <td className="px-4 py-3 text-slate-600">{u.company || "—"}</td>
                  <td className="px-4 py-3">
                    {u.role === "admin" ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-[#991B1B] text-white">
                        <Shield className="w-3 h-3" /> Admin
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-700">
                        <UserIcon className="w-3 h-3" /> Kunde
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500">{new Date(u.created_at).toLocaleDateString("de-CH")}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setSel(u)} className="text-[#991B1B] hover:underline text-sm font-medium">Bearbeiten</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {sel && <UserEditDrawer user={sel} onClose={() => setSel(null)} onSave={(p) => update(sel.id, p)} onDelete={() => del(sel.id)} />}
    </div>
  );
}

function UserEditDrawer({ user, onClose, onSave, onDelete }) {
  const [f, setF] = useState({ first_name: user.first_name, last_name: user.last_name, company: user.company || "", phone: user.phone || "", role: user.role, new_password: "" });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  return (
    <div className="fixed inset-0 z-[10001] bg-black/40 flex items-stretch justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="mbw-overline mb-2">Benutzer</div>
            <h2 className="font-heading text-2xl font-bold text-[#1A2433]">{user.first_name} {user.last_name}</h2>
            <div className="text-sm text-slate-500">{user.email}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Vorname" value={f.first_name} onChange={(v) => set("first_name", v)} />
            <Field label="Nachname" value={f.last_name} onChange={(v) => set("last_name", v)} />
          </div>
          <Field label="Firma" value={f.company} onChange={(v) => set("company", v)} />
          <Field label="Telefon" value={f.phone} onChange={(v) => set("phone", v)} />
          <div>
            <label className="block text-sm font-medium text-[#1A2433] mb-2">Rolle</label>
            <select value={f.role} onChange={(e) => set("role", e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white">
              <option value="customer">Kunde</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <Field label="Neues Passwort (optional)" type="password" value={f.new_password} onChange={(v) => set("new_password", v)} />
        </div>

        <div className="mt-8 flex justify-between gap-3">
          <button onClick={onDelete} className="text-red-600 hover:text-red-700 font-medium text-sm">Löschen</button>
          <button onClick={() => onSave(f)} className="btn-primary">Speichern</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#1A2433] mb-2">{label}</label>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#991B1B]" />
    </div>
  );
}
